import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Modal, Form, Input, Button, message, Select, notification } from 'antd';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useIntercom } from 'react-use-intercom';
import { convertFromHTML, convertToRaw, ContentState } from 'draft-js';
import _ from 'lodash';
import PropTypes from 'prop-types';

import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import RestoreProposalModal from 'components/RestoreProposalModal/RestoreProposalModal';
import Popconfirm from 'components/PopConfirm';
import CreateDuplicateProposalModal from 'components/CreateDuplicateProposalModal';
import ShareWithModal from 'components/ShareWithModal';
import AddUser from 'pages/UsersTeam/components/AddUser';
import Path from 'routes/path';
import utils from 'utils/utils';
import CloseIcon from 'components/Icons/CloseIcon';
import DropdownIcon from 'components/Icons/DropdownIcon';
import useWindowDimensions from 'hooks/useWindowDimensions';
import MoveLibraryModal from 'pages/ContentLibrary/ContentLibraryModals/MoveLibraryModal';
import useTreeHandler from 'hooks/treeHandler';

import GenerateInvoiceModal from './GenerateInvoiceModal';
import NewProposalModal from './NewProposalModal';
import OnboardingModal from './OnboardingModal';
import { PROPOSAL_SUBSCRIPTION_TOPIC, PROPOSAL_FILTER_ITEMS } from 'constants/index';
import { MANAGE_TEAM_MEMBERS } from 'graphql/mutations/usersTeamMutation';
import { USER_UPDATE } from 'graphql/mutations/userMutations';
import { GET_TREEVIEW_DATA } from 'graphql/queries/contentLibraryQueries';
import { FETCH_PIPEDRIVE_DEALS_CLIENT_DETAILS } from 'graphql/queries/integrationQueries';
import { CONTENT_LIBRARY_ADD_CHILD } from 'graphql/mutations/contentLibraryMutations';
import { sanitizeObject } from 'utils/xss';
import { getProposalTitle } from 'helpers/proposal';
import {
  filterTreeData,
  handleModalTreeSearchWrapper,
  addNewChildFromSearch,
} from 'helpers/contentlibrary';
import {
  BULK_DELETE_PROPOSALS_MUTATION,
  BULK_UPDATE_PROPOSALS_MUTATION,
  CREATE_PROPOSAL_MUTATION,
  DELETE_PROPOSAL_FILES,
} from 'graphql/mutations/proposalMutation';
import {
  cache,
  userVar,
  userProfileVar,
  proposalsVar,
  mondayBoardIdVar,
  fromAppVar,
  hubspotUrlParams,
} from 'graphql/cache';
import {
  HubspotClientName,
  PipedriveClientName,
  PipedriveDealName,
  MondayClientName,
  WixClientName,
  ClientName,
} from 'pages/Proposal/modules';
import { ActivityModal } from './ActivityModal/ActivityModal';

const daystrial = parseInt(process.env.REACT_APP_DAYS_TRAIL) || 14;

/**
 * Dashboard modals
 * @param {object} user - User
 * @param {function} refetchUser - Refetch user
 * @param {boolean} trash - Trash
 * @param {array} proposals - Proposals
 * @param {array} teamMembers - Team members
 * @param {string} selectedProposalId - Selected proposal ID
 * @param {object} popConfirmDetails - Pop confirm details
 * @param {function} setPopConfirmDetails - Set pop confirm details
 * @param {boolean} showAddProposalModal - Show add proposal modal
 * @param {function} setShowAddProposalModal - Set show add proposal modal
 * @param {boolean} confirmModal - Confirm modal
 * @param {function} showConfirmModal - Show confirm modal
 * @param {boolean} showAddUserModal - Show add user modal
 * @param {function} setShowAddUserModal - Set show add user modal
 * @param {boolean} duplicateModal - Duplicate modal
 * @param {function} showDuplicateModal - Show duplicate modal
 * @param {function} setUserInfoModal - Set user info modal
 * @param {boolean} permissionErrorModal - Permission error modal
 * @param {function} showPermissionErrorModal - Show permission error modal
 * @param {boolean} restoreModal - Restore modal
 * @param {function} showRestoreModal - Show restore modal
 * @param {string} generateInvoiceModal - Generate invoice modal
 * @param {function} setGenerateInvoiceModal - Set generate invoice modal
 * @param {boolean} userInfoModal - User info modal
 * @param {function} showShareProposalModal - Show share proposal modal
 * @param {function} setShowShareProposalModal - Set show share proposal modal
 * @param {function} refetchProposal - Refetch proposal
 * @param {boolean} moveModal - Move modal
 * @param {function} showMoveModal - Show move modal
 * @param {function} fetchMoreProposal - Fetch more proposal
 * @param {boolean} disabledEditingModal - Disabled editing modal
 * @param {function} setDisabledEditingModal - Set disabled editing modal
 * @param {function} setLoadingPipedriveDealsDetails - Set loading pipedrive deals details
 * @param {function} findProposalById - Find proposal by ID
 * @param {array} selectedProposalIds - Selected proposal IDs
 * @param {function} setSelectedProposalIds - Set selected proposal IDs
 */
const DashboardModals = ({
  user,
  refetchUser,
  trash,
  proposals,
  teamMembers,
  selectedProposalId,
  popConfirmDetails,
  setPopConfirmDetails,
  showAddProposalModal,
  setShowAddProposalModal,
  confirmModal,
  showConfirmModal,
  showAddUserModal,
  setShowAddUserModal,
  duplicateModal,
  showDuplicateModal,
  setUserInfoModal,
  permissionErrorModal,
  showPermissionErrorModal,
  permissionErrorModalTitle,
  permissionErrorModalBody,
  selectedProposal,
  restoreModal,
  showRestoreModal,
  generateInvoiceModal,
  setGenerateInvoiceModal,
  userInfoModal,
  showShareProposalModal,
  setShowShareProposalModal,
  refetchProposal,
  moveModal,
  showMoveModal,
  fetchMoreProposal,
  disabledEditingModal,
  setDisabledEditingModal,
  setLoadingPipedriveDealsDetails,
  findProposalById,
  selectedProposalIds,
  setSelectedProposalIds,
  modal,
}) => {
  const userVariables = userVar();
  const history = useHistory();
  const { trackEvent } = useIntercom();
  const { allowedEditing } = useWindowDimensions();

  /**
   * Use tree handler
   */
  const {
    treeData,
    handleTreeData,
    setIndexes,
    initialTreeData,
    setInitialTreeData,
    addKey,
    selectedFolderKey,
  } = useTreeHandler();

  /**
   * Filtered tree data
   */
  const filteredTreeData = filterTreeData(treeData, 'templates');

  /**
   * Local state
   */
  const [isSaving, setIsSaving] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [plan, setPlan] = useState('monthly');
  const [showProspectFormModal, setShowProspectFormModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [pipedriveDealNameId, setPipedriveDealNameId] = useState('');
  const [hubspotContactId, sethubspotContactId] = useState('');
  const [newExternalProposalId, setNewExternalProposalId] = useState(null);
  const [externalProposalFile, setExternalProposalFile] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState('');
  const [modalTreeData, setModalTreeData] = useState({ children: [] });
  let [externalProposalFileType, setExternalProposalFileType] = useState(null);
  // const [selectedFolderKey, setSelectedFolderKey] = useState('');
  // const [selectedFolderLevel, setSelectedFolderLevel] = useState('');
  // const [selectedFolderName, setSelectedFolderName] = useState('');

  /**
   * Update modal tree data when context tree data changes
   */
  useEffect(() => {
    setModalTreeData(filteredTreeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData]);

  const [form] = Form.useForm();

  /**
   * Fetch tree view
   */
  const [fetchTreeView, { loading: loadingTreeView }] = useLazyQuery(GET_TREEVIEW_DATA, {
    fetchPolicy: 'network-only',
  });

  /**
   * Handle refetch
   *
   */
  const handleRefetch = useCallback(async () => {
    fetchTreeView({
      variables: {
        type: 'templates',
      },
      onCompleted: ({ fetchContentLibrary }) => {
        let tempTreeData = JSON.parse(JSON.stringify(fetchContentLibrary || {}));
        tempTreeData = setIndexes(tempTreeData);
        handleTreeData({
          data: JSON.parse(JSON.stringify(tempTreeData)) || {},
          type: 'templates',
        });
        setInitialTreeData(JSON.parse(JSON.stringify(fetchContentLibrary)));
      },
    });
  }, [fetchTreeView, setIndexes, handleTreeData, setInitialTreeData]);

  /**
   * Add child mutation
   */
  const [addChild] = useMutation(CONTENT_LIBRARY_ADD_CHILD, {
    onCompleted() {
      notification.success({
        description: 'Created new folder successfully.',
      });
      handleRefetch();
    },
    onError() {},
  });

  /**
   * This query in content library tree is getting executed after get all templates query which is causing no templates to load
   */
  useEffect(() => {
    if (moveModal) {
      handleRefetch();
    }
  }, [moveModal, handleRefetch]);

  /**
   * Fetch pipedrive deal client details query
   */
  const [fetchPipedriveDealClientDetails] = useLazyQuery(FETCH_PIPEDRIVE_DEALS_CLIENT_DETAILS, {
    skip: !user._id,
    fetchPolicy: 'network-only',
    onCompleted: ({ fetchPipedriveDealsClientDetails }) => {
      setClientName(fetchPipedriveDealsClientDetails?.dealClientName);
      setClientEmail(fetchPipedriveDealsClientDetails?.dealClientEmail);
      setPipedriveDealNameId(fetchPipedriveDealsClientDetails?.dealId);
      form.setFieldsValue({ pipedrive_deal_id: fetchPipedriveDealsClientDetails?.dealId });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  /**
   * Use effect
   */
  useEffect(() => {
    const pipedriveSelectedId = utils.readCookie('pipedriveSelectedId');
    if (pipedriveSelectedId) {
      fetchPipedriveDealClientDetails({
        variables: {
          pipedriveSelectedId,
          pipedriveResource: utils.readCookie('pipedriveResource'),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPipedriveDealClientDetails]);

  const language =
    user?.profile?.language === 'hebrew' ? 'hebrew' : user?.language === 'arabic' ? 'arabic' : '';

  // Get available integrations
  const getAvailableIntegrations = () => {
    const integrations = [];

    if (userVariables?.pipedrive) {
      integrations.push({ value: 'pipedrive', label: 'Pipedrive' });
    }

    if (Object.keys(userVariables?.monday || {})?.length > 0) {
      integrations.push({ value: 'monday', label: 'Monday.com' });
    }

    if (userVariables.hubspot?.token?.expires_in) {
      integrations.push({ value: 'hubspot', label: 'Hubspot' });
    }

    if (userVariables?.wix?.length) {
      integrations.push({ value: 'wix', label: 'Wix' });
    }

    return integrations;
  };

  const availableIntegrations = getAvailableIntegrations();

  // Auto-select integration only when coming from specific apps
  useEffect(() => {
    if (availableIntegrations?.length && !selectedIntegration) {
      if (fromAppVar() === 'hubspot') {
        setSelectedIntegration('hubspot');
        form.setFieldsValue({ integration: 'hubspot' });
      } else if (fromAppVar() === 'monday') {
        setSelectedIntegration('monday');
        form.setFieldsValue({ integration: 'monday' });
      }
    }
  }, [availableIntegrations, form, selectedIntegration]);

  /**
   *
   * @param {*} value Name
   * @param {*} _ email
   * @param {*} __ company
   * @param {*} ___ Monday Item ID
   * @param {*} pipedriveDealName Pipedrive Deal Name
   * @param {*} pipedriveDealId Pipedrive Deal ID
   * @param {*} hubspotContactId Hubspot Contact ID
   */
  const onInputChange = (
    value,
    _,
    __,
    ___,
    pipedriveDealName,
    pipedriveDealId,
    hubspotContactId
  ) => {
    if (hubspotContactId) {
      sethubspotContactId(hubspotContactId);
    } else if (pipedriveDealId) {
      form.setFieldsValue({ pipedrive_deal_id: pipedriveDealId });
      setPipedriveDealNameId(pipedriveDealName);
    } else {
      setClientName(value);
    }
  };

  const onCreateNewFormClick = () => {
    form
      .validateFields()
      .then((values) => {
        createFromExternalProposal({ values, proposalPID: newExternalProposalId });
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  };

  const [deleteProposalFiles] = useMutation(DELETE_PROPOSAL_FILES);

  const onDeleteProposalFiles = (tempExternalProposalFile, tempExternalProposalFileType) => {
    if (
      (externalProposalFile?.length > 0 && externalProposalFileType !== 'docx') ||
      (tempExternalProposalFile?.length > 0 && tempExternalProposalFileType !== 'docx')
    ) {
      const tempExternalProposalFiles = externalProposalFile || tempExternalProposalFile;

      deleteProposalFiles({
        variables: {
          urls: tempExternalProposalFiles.map(
            (tempExternalProposalFile) => tempExternalProposalFile.url
          ), //el.url,
        },
      })
        .then(({ data }) => {})
        .catch((error) => {
          console.error('External Proposal File deletion failed', error);
        });
    }
    setExternalProposalFile(null);
    setExternalProposalFileType(null);
    setShowProspectFormModal(false);
  };

  /**
   * Parse element children
   * @param {*} docFile
   * @returns
   */
  const parseElChildren = (docFile) => {
    let newsection = {
      title: '',
      text: '',
      raw: {
        entityMap: {},
        blocks: [],
      },
    };

    if (docFile.length > 0) {
      docFile.forEach(async (elm, idx) => {
        let elNodeName = elm.nodeName;

        if (elNodeName === 'TABLE') {
          let data = [];
          elm.children.forEach((eltbody, idt) => {
            eltbody.children.forEach((eltr, idb) => {
              data[idb] = {};
              data[idb].id = idb + 1;
              eltr.children.forEach((eltd, idd) => {
                data[idb][idd + 1] = eltd.innerText;
              });
            });
          });

          const keyValue = Object.keys(newsection.raw.entityMap).length;

          newsection.raw.blocks.push({
            key: _.times(4, () => _.random(35).toString(36)).join(''),
            text: ' ',
            type: 'atomic',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [
              {
                offset: 0,
                length: 1,
                key: keyValue ? keyValue : 0,
              },
            ],
            data: {},
          });

          newsection.raw.entityMap = {
            ...newsection.raw.entityMap,
            [keyValue]: {
              type: 'table',
              mutability: 'MUTABLE',
              data: {
                data: data,
                config: {
                  topRowColor: 'lightgrey',
                  rowColor: 'white',
                },
              },
            },
          };
        } else if (elNodeName === 'UL' || elNodeName === 'OL') {
          elm.children.forEach((eltbody, idt) => {
            const blocksFromHTML = convertFromHTML(eltbody.innerHTML);
            const state = ContentState.createFromBlockArray(
              blocksFromHTML.contentBlocks,
              blocksFromHTML.entityMap
            );
            const rawElement = convertToRaw(state);
            rawElement.blocks.forEach((block) => {
              newsection.raw.blocks.push({
                key: _.times(4, () => _.random(35).toString(36)).join(''),
                text: block.text,
                type: elNodeName === 'UL' ? 'unordered-list-item' : 'ordered-list-item',
                depth: 0,
                inlineStyleRanges: block.inlineStyleRanges,
                entityRanges: [],
                data: {},
              });
            });
          });
        } else {
          const innerHTMLs = elm.innerHTML.replaceAll('><', '>-new-<').split('-new-');
          innerHTMLs.forEach((innerHTML) => {
            const blocksFromHTML = convertFromHTML(innerHTML);
            const state = ContentState.createFromBlockArray(
              blocksFromHTML.contentBlocks,
              blocksFromHTML.entityMap
            );
            const rawElement = convertToRaw(state);
            const rawBlocks = rawElement.blocks.map((block) => {
              if (block.entityRanges.length) {
                const entityRanges = block.entityRanges.map((entityRange, entityIndex) => {
                  const keyValue = Object.keys(newsection.raw.entityMap).length;
                  newsection.raw.entityMap = {
                    ...newsection.raw.entityMap,
                    [keyValue]: rawElement.entityMap[entityIndex],
                  };
                  return { ...entityRange, key: keyValue };
                });
                return { ...block, type: 'atomic', text: ' ', entityRanges };
              }
              return block;
            });

            newsection.raw.blocks = newsection.raw.blocks.concat(rawBlocks);
          });
        }
      });
    }
    return newsection;
  };

  /**
   * Create from external proposal
   * @param {*} values
   * @param {*} proposalPID
   */
  const createFromExternalProposal = ({ values, proposalPID }) => {
    setIsLoading(true);

    const { client_name: newClientName, project_name: newProjectName, pipedrive_deal_id } = values;

    const sectionOrderIdArray = [];
    const sectionOrderObject = {};

    if (externalProposalFileType === 'docx') {
      const sectionId = uuidv4();
      sectionOrderIdArray.push(sectionId);
      sectionOrderObject[sectionId] = parseElChildren(externalProposalFile);

      const sectionPosition = {
        contact: 'block',
        cover: 'top',
        coverTextAlignment: 'left',
        position: 'none',
      };
      sectionOrderObject['titleFont'] = sectionPosition;
    } else if (externalProposalFileType === 'pdf') {
      let newsection = {
        image: '<img />',
        raw: {
          entityMap: {},
          blocks: [],
        },
      };

      externalProposalFile
        .sort((a, b) => a.uploadId - b.uploadId)
        .forEach((elm) => {
          const keyValue = Object.keys(newsection.raw.entityMap).length;

          newsection.raw.blocks.push({
            key: _.times(4, () => _.random(35).toString(36)).join(''),
            text: ' ',
            type: 'atomic',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [
              {
                offset: 0,
                length: 1,
                key: keyValue ? keyValue : 0,
              },
            ],
            data: {},
          });

          newsection.raw.entityMap = {
            ...newsection.raw.entityMap,
            [keyValue]: {
              type: 'image',
              mutability: 'MUTABLE',
              data: {
                src: elm.url,
              },
            },
          };
        });

      sectionOrderIdArray.push('imported');
      sectionOrderObject['imported'] = newsection;
    } else {
      externalProposalFileType = utils.getFileExtension(externalProposalFile[0].url).toLowerCase();
      if (
        externalProposalFileType === 'png' ||
        externalProposalFileType === 'jpeg' ||
        externalProposalFileType === 'jpg'
      ) {
        let newsection = {
          image: '<img />',
          raw: {
            entityMap: {},
            blocks: [],
          },
        };

        const keyValue = Object.keys(newsection.raw.entityMap).length;

        newsection.raw.blocks.push({
          key: _.times(4, () => _.random(35).toString(36)).join(''),
          text: ' ',
          type: 'atomic',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [
            {
              offset: 0,
              length: 1,
              key: keyValue ? keyValue : 0,
            },
          ],
          data: {},
        });

        newsection.raw.entityMap = {
          ...newsection.raw.entityMap,
          [keyValue]: {
            type: 'image',
            mutability: 'MUTABLE',
            data: {
              src: externalProposalFile[0].url,
            },
          },
        };

        sectionOrderIdArray.push('imported');
        sectionOrderObject['imported'] = newsection;
      }
    }

    const externalProposalDoc = {
      draftSectionOrderIds: sectionOrderIdArray,
      draftSectionElement: sectionOrderObject,
    };

    createProposal({
      variables: {
        pid: proposalPID,
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`,
        template: {
          newProjectName: newProjectName,
          newClientName: newClientName,
          newClientMail: clientEmail,
        },
        additionalInfo: {
          monday: {
            boardId: mondayBoardIdVar(),
          },
          hubspot: {
            hubspotObjectType: hubspotUrlParams().from || 'contact',
            hubspotContactId: hubspotContactId || '',
            hubspotDealId: hubspotUrlParams().hubspotDealId || '',
            hubspotCompanyId: hubspotUrlParams().hubspotCompanyId || '',
          },
          pipedrive: {
            pipedriveDealId: pipedrive_deal_id,
          },
        },
        importedProposal:
          externalProposalFileType === 'docx' ||
          externalProposalFileType === 'pdf' ||
          externalProposalFileType === 'jpeg' ||
          externalProposalFileType === 'jpg' ||
          externalProposalFileType === 'png'
            ? externalProposalDoc
            : externalProposalFile,
      },
    })
      .then(({ data }) => {
        cache.evict('loadProposals');
        const {
          createProposal: { pid: newProposalId, _id },
        } = data;

        trackEvent('wizard-init', {
          id: _id,
          pid: newProposalId,
        });

        utils.deleteCookie('pipedriveSelectedId');
        utils.deleteCookie('pipedriveResource');
        utils.deleteCookie('pipedriveToken');

        setIsLoading(false);

        history.push(`/d/${newProposalId}`);
      })
      .catch((error) => {
        setIsLoading(false);
        message.error('Proposal create failed');
      });
  };

  /**
   * Handle manage team members response
   * @param {*} member
   * @param {*} errorCallback
   */
  const handleManageTeamMembersResponse = (member, errorCallback) => {
    if (member.error) {
      message.error(
        `User ${member.user.emails[0].address} addtion failed: ${
          member.error.match(/E11000/) ? 'User already exists' : member.error
        }`
      );
      errorCallback();
    } else {
      message.success(`User ${member.user.emails[0].address} added successfully`);
    }
  };

  const [createProposal] = useMutation(CREATE_PROPOSAL_MUTATION);

  const [bulkDeleteProposals, { loading: isBulkDeleting }] = useMutation(
    BULK_DELETE_PROPOSALS_MUTATION,
    {
      onCompleted({ bulkDeleteProposals: result }) {
        cache.evict('loadProposals');
        showConfirmModal(false);
        setSelectedProposalIds?.([]);

        if (!result?.deletedIds?.length && !result?.failedIds?.length) return;

        const deletedSet = new Set(result?.deletedIds);
        const updatedProposals = proposals?.filter((prop) => !deletedSet?.has(prop._id));

        proposalsVar(updatedProposals);

        const deletedProposalsCount = result?.deletedIds?.length ?? 0;
        const failedProposalsCount = result?.failedIds?.length ?? 0;

        if (failedProposalsCount > 0) {
          message.warning(
            `${deletedProposalsCount} proposal(s) deleted; ${failedProposalsCount} failed.`
          );
        } else {
          message.success(
            deletedProposalsCount > 1
              ? 'Proposals deleted successfully'
              : 'Proposal deleted successfully'
          );
          fetchMoreProposal('delete');
        }
      },
      onError() {
        message.error('Proposal delete failed');

        showConfirmModal(false);
        setSelectedProposalIds?.([]);
      },
    }
  );

  const [bulkUpdateProposals] = useMutation(BULK_UPDATE_PROPOSALS_MUTATION);

  const [manageTeamMembers] = useMutation(MANAGE_TEAM_MEMBERS, {
    onCompleted: ({ manageTeamMembers }) => {
      let error = false;

      const errorCallback = () => {
        error = true;
      };

      manageTeamMembers?.add?.forEach?.((member) =>
        handleManageTeamMembersResponse(member, errorCallback)
      );
      manageTeamMembers?.delete?.forEach?.((member) =>
        handleManageTeamMembersResponse(member, errorCallback)
      );

      if (!error) {
        setShowAddUserModal(false);
      }
      setIsSaving(false);
    },
    onError: (error) => {
      setIsSaving(false);
      message.error(`User add failed: ${error.message}`);
    },
  });

  const [saveUser] = useMutation(USER_UPDATE, {
    async onCompleted({ updateUser }) {
      utils.loginredirect(user, history);

      userVar({ ...user, ...updateUser });
      userProfileVar({
        email: updateUser.emails[0].address,
        ...updateUser.profile,
      });
    },
  });

  /**
   * Create new proposal
   * @param {*} proposalType
   * @param {*} proposalFile
   * @param {*} proposalFileType
   */
  const createNewProposal = ({ proposalType, proposalFile, proposalFileType }) => {
    setShowAddProposalModal(false);
    if (proposalType === 'template') {
      history.push(`${Path.CONTENT_LIBRARY}${history.location.search}`);
    } else {
      if (!allowedEditing) {
        setDisabledEditingModal(true);
        // message.error('Proposals creation is only available on the desktop version.');
      } else {
        setLoadingPipedriveDealsDetails(true);
        const pid = uuidv4();
        setNewExternalProposalId(pid);

        setExternalProposalFile(proposalFile);
        setExternalProposalFileType(proposalFileType);

        if (proposalType === 'external-proposal' || proposalType === 'external-proposal-doc') {
          setShowProspectFormModal(true);
          setLoadingPipedriveDealsDetails(false);
        } else {
          const additionalInfo = {
            monday: {
              boardId: mondayBoardIdVar(),
            },
            hubspot: {
              hubspotObjectType: hubspotUrlParams().from || 'contact',
              hubspotContactId: hubspotContactId || hubspotUrlParams().hubspotContactId || '',
              hubspotDealId: hubspotUrlParams().hubspotDealId || '',
              hubspotCompanyId: hubspotUrlParams().hubspotCompanyId || '',
            },
          };

          if (utils.readCookie('pipedriveSelectedId') && utils.readCookie('pipedriveToken')) {
            additionalInfo.pipedrive = {
              pipedriveSelectedId: utils.readCookie('pipedriveSelectedId'),
              pipedriveResource: utils.readCookie('pipedriveResource'),
              pipedriveToken: utils.readCookie('pipedriveToken'),
            };
          }

          createProposal({
            variables: {
              pid,
              topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`,
              additionalInfo,
            },
          })
            .then(({ data }) => {
              cache.evict('loadProposals');
              const {
                createProposal: { pid: newProposalId, _id },
              } = data;

              trackEvent('wizard-init', {
                id: _id,
                pid: newProposalId,
              });

              utils.deleteCookie('pipedriveToken');
              utils.deleteCookie('pipedriveResource');
              utils.deleteCookie('pipedriveSelectedId');

              history.push(`/w/${newProposalId}`);
            })
            .catch((error) => {
              message.error('Proposal create failed');
            })
            .finally(() => {
              setLoadingPipedriveDealsDetails(false);
            });
        }
      }
    }
  };

  /**
   * Confirm delete proposal (single batch request for all selected).
   */
  const confirmDeleteProposal = () => {
    const ids = selectedProposalIds?.length ? selectedProposalIds : [selectedProposalId];

    if (!ids?.length) return;

    const topic = `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`;
    let proposalList = ids?.map((id) => findProposalById(id));

    if (proposalList?.some((proposal) => proposal?.lockedData?.value)) {
      const lockedProposals = proposalList?.filter((proposal) => proposal?.lockedData?.value);

      lockedProposals.forEach((proposal) => {
        message.error(
          `Proposal ${utils.changeVariable(
            proposal?.variables,
            getProposalTitle({ proposal, user }),
            user?.profile?.dateFormat
          )} is locked and cannot be deleted.`
        );
        proposalList = proposalList?.filter((p) => p?._id !== proposal?._id);
      });
    }

    const pidList = proposalList?.map((p) => p?.pid ?? p?._id);

    if (pidList?.length === 0) {
      message.info('No proposals to delete');
      showConfirmModal(false);
      setSelectedProposalIds?.([]);
      return;
    }

    bulkDeleteProposals({
      variables: {
        topic,
        pidList,
        remove: !!trash,
      },
    });
  };

  /**
   * Duplicate proposal
   * @param {*} values
   * @param {*} proposal
   */
  const duplicateProposal = ({ values, proposal }) => {
    const version = proposal.version || 0;
    if (version < 5) {
      return;
    }

    values = sanitizeObject(values);

    window.ga('send', 'event', 'prop-duplicated', proposal._id, window.location.href);
    trackEvent('dup-prop', {
      id: proposal._id,
      pid: proposal.pid,
      url: window.location.origin + '/pd/' + proposal._id,
    });

    const { client_name: newClientName, project_name: newProjectName, hubspot_contact_id } = values;
    setIsDuplicating(true);
    createProposal({
      variables: {
        pid: proposal.pid,
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`,
        duplicate: {
          newProjectName: newProjectName,
          newClientName: newClientName,
        },
        additionalInfo: {
          monday: {
            boardId: mondayBoardIdVar(),
          },
          hubspot: {
            hubspotObjectType: hubspotUrlParams().from || 'contact',
            hubspotContactId: hubspot_contact_id || '',
            hubspotDealId: hubspotUrlParams().hubspotDealId || '',
            hubspotCompanyId: hubspotUrlParams().hubspotCompanyId || '',
          },
          pipedrive: {
            pipedriveDealId: values.pipedrive_deal_id,
          },
        },
      },
    })
      .then((data) => {
        showDuplicateModal(false);
        if (data) {
          refetchProposal('', [], [], PROPOSAL_FILTER_ITEMS);
          setIsDuplicating(false);
          message.success('Proposal duplicated successfully');
        } else {
          message.error(`Problem duplicating proposal`);
        }
      })
      .catch((error) => {
        setIsDuplicating(false);
        message.error(`Problem duplicating proposal ${error.message}`);
      });
  };

  /**
   * Retrieve proposal (single batch request for all selected).
   */
  const retrieveProposal = async () => {
    const ids = selectedProposalIds?.length ? selectedProposalIds : [selectedProposalId];

    if (!ids?.length) return;

    setIsRestoring(true);

    const topic = `${PROPOSAL_SUBSCRIPTION_TOPIC}_${user?.teamId || user?._id}`;

    const updates = ids
      ?.map((proposalId) => {
        const proposal = findProposalById(proposalId);
        if (!proposal) return null;
        return {
          updateType: 'restore',
          proposal: {
            id: proposal._id,
            _id: proposal._id,
            pid: proposal.pid,
            uid: proposal.uid,
            state: proposal.prevState,
            prevState: proposal.state,
            published: '',
          },
        };
      })
      ?.filter(Boolean);

    try {
      const { data } = await bulkUpdateProposals({
        variables: { topic, updates },
      });
      const result = data?.bulkUpdateProposals;
      const restoredIds = (result?.updated ?? [])?.map((p) => p._id);
      const updatedProposals = proposals?.filter((prop) => !restoredIds.includes(prop._id));

      proposalsVar(updatedProposals);

      const n = restoredIds?.length ?? 0;
      const f = result?.failedIds?.length ?? 0;

      if (f > 0) {
        message.warning(`${n} proposal(s) restored; ${f} failed.`);
      } else {
        message.success(
          n > 1 ? `${n} proposals restored successfully` : 'Proposal restored successfully'
        );
      }
    } catch (err) {
      message.error('Restore failed');
    } finally {
      setIsRestoring(false);
      showRestoreModal(false);
      setSelectedProposalIds?.([]);
    }
  };

  /**
   * Create proposal signup
   */
  const createProposalSignup = () => {
    setUserInfoModal(false);
    setShowAddProposalModal(true);
  };

  /**
   * Add user
   */
  const addUser = () => {
    setUserInfoModal(false);
    setShowAddUserModal(true);
  };

  /**
   * On add user cancel
   */
  const onAddUserCancel = () => {
    setShowAddUserModal(false);
  };

  /**
   * Save user profile info
   * @param {*} values
   */
  const saveUserProfileInfo = (values) => {
    const {
      fullname,
      where,
      currency,
      password,
      dateFormat,
      defaultProposalFont,
      companyname,
      domain,
      ptype,
      otherptype,
      proposalLanguage,
      enableAITool,
      brandlogo,
      brandcolor,
      onBoardComplete,
      onboardingState,
      image,
    } = values;

    const profile = Object.assign(
      { ...user.profile },
      {
        name: fullname,
        where: where,
        language: proposalLanguage,
        terms: 'true',
        currency,
        enableAITool,
        dateFormat,
        companyname,
        onBoardComplete,
        onboardingState,
        image,
      }
    );

    const branding = Object.assign(
      { ...user.branding },
      {
        buttonColor: brandcolor || user?.branding?.buttonColor,
        companyLogo: brandlogo || user?.branding?.companyLogo,
        font: defaultProposalFont || user?.branding?.font,
      }
    );

    saveUser({
      variables: {
        user: {
          id: user._id,
          password: password,
          profile: profile,
          branding: branding,
          ptype: ptype || user.ptype,
          otherptype: otherptype || user.otherptype,
          domain,
        },
      },
    });
  };

  /**
   * Get trial time
   * @returns
   */
  const getTrialTime = () => {
    if (!user) {
      return 666;
    }

    const timespan = new Date() - new Date(user.stopperAnchor || user.createdAt);
    const dayspan = timespan / 1000 / 60 / 60 / 24;

    return daystrial - dayspan;
  };

  /**
   * Render handle tree search
   *
   * @param {string} tempSearchValue
   */
  const renderHandleTreeSearch = (tempSearchValue) => {
    handleModalTreeSearchWrapper({
      tempSearchValue,
      treeData: filteredTreeData,
      setIndexes,
      initialTreeData,
      addKey,
      setModalTreeData,
      filteredTreeData,
    });
  };

  /**
   * Render add new child from search
   *
   * @param {string} treeSearchValue
   */
  const renderAddNewChildFromSearch = (treeSearchValue) =>
    addNewChildFromSearch({
      treeSearchValue,
      selectedFolderKey,
      user,
      treeData: modalTreeData,
      addChild,
    });

  if (modal?.type === 'open-logs-modal') {
    return <ActivityModal {...modal} />;
  }

  /**
   * Render
   */
  return (
    <>
      {popConfirmDetails.show && (
        <Popconfirm
          title={popConfirmDetails.title}
          body={popConfirmDetails.body}
          visible={popConfirmDetails.show}
          cancelText={popConfirmDetails.cancelText}
          confirmText={popConfirmDetails.confirmText}
          modalClassName={popConfirmDetails.className}
          onCancel={() =>
            setPopConfirmDetails((prevState) => ({
              ...prevState,
              show: false,
            }))
          }
          onConfirm={popConfirmDetails.onConfirm}
        />
      )}

      {showAddProposalModal && (
        <NewProposalModal
          visible={showAddProposalModal}
          onCancel={() => setShowAddProposalModal(false)}
          createNewProposal={createNewProposal}
        />
      )}

      {showProspectFormModal && (
        <Modal
          className="simple-info-modal create-duplicate-modal"
          visible={showProspectFormModal}
          okText="CONTINUE"
          centered
          cancelText="CANCEL"
          closeIcon={<CloseIcon className="close-icon" />}
          footer={null}
          onCancel={() => setShowProspectFormModal(false)}
          maskClosable={false}>
          <h3 className="title">Create Proposal from File</h3>
          <div className="divider" />

          <div className="body">
            <Form
              form={form}
              layout="vertical"
              name="form_in_modal"
              className="form"
              initialValues={{
                modifier: 'public',
                project_name: 'Imported Proposal',
                client_name: clientName,
              }}>
              <Form.Item
                name="project_name"
                label="Project Name"
                rules={[
                  {
                    required: true,
                    message: 'Project name is required.',
                  },
                  {
                    whitespace: true,
                    message: 'Project name is required.',
                  },
                ]}>
                <Input />
              </Form.Item>
              {availableIntegrations?.length >= 1 && (
                <Form.Item label="Select Integration (Optional)" name="integration">
                  <div className="create-duplicate-integration-selector">
                    <Select
                      placeholder="Choose an integration or leave blank"
                      className="wizard-integration-selector-dropdown"
                      value={selectedIntegration}
                      onChange={(value) => {
                        setSelectedIntegration(value);
                        form.setFieldsValue({ integration: value });
                      }}
                      allowClear
                      onClear={() => {
                        setSelectedIntegration('');
                        form.setFieldsValue({ integration: undefined });
                      }}
                      options={availableIntegrations}
                      suffixIcon={<DropdownIcon />}
                    />
                  </div>
                </Form.Item>
              )}

              <Form.Item
                name="client_name"
                label="Prospect Name"
                rules={[
                  {
                    required: true,
                    message: 'Prospect name is required',
                  },
                  {
                    whitespace: true,
                    message: 'Prospect name is required.',
                  },
                ]}>
                {selectedIntegration === 'monday' ? (
                  <MondayClientName
                    type="setContact"
                    action="contact"
                    clientNameValue={''}
                    alert={true}
                    onChange={onInputChange}
                    fromTemplate={true}
                  />
                ) : selectedIntegration === 'wix' ? (
                  <WixClientName
                    type="setContact"
                    action="contact"
                    clientNameValue={''}
                    onChange={onInputChange}
                    fromTemplate={true}
                    language={language}
                  />
                ) : selectedIntegration === 'pipedrive' ? (
                  <PipedriveClientName
                    type="setContact"
                    action="contact"
                    clientNameValue={clientName}
                    onChange={onInputChange}
                    fromTemplate={true}
                    language={language}
                  />
                ) : selectedIntegration === 'hubspot' ? (
                  <HubspotClientName
                    type="setContact"
                    action="contact"
                    clientNameValue={clientName}
                    onChange={onInputChange}
                    fromTemplate={true}
                    language={language}
                  />
                ) : (
                  <ClientName
                    type="setContact"
                    action="contact"
                    onChange={onInputChange}
                    placeholder="CONTACT PERSON NAME"
                    fromTemplate={true}
                    language={language}
                  />
                )}
              </Form.Item>
              {selectedIntegration === 'pipedrive' && (
                <Form.Item name="pipedrive_deal_id" label="Deal">
                  <PipedriveDealName
                    type="setDeal"
                    action="deal"
                    dealNameValue={pipedriveDealNameId}
                    onChange={onInputChange}
                    fromTemplate={true}
                    language={user?.profile?.language}
                  />
                </Form.Item>
              )}
            </Form>
          </div>

          <div className="button-wrapper">
            <Button
              className="large-btn secondary-btn"
              type="primary"
              loading={isLoading}
              onClick={onCreateNewFormClick}>
              Create
            </Button>
            <Button
              className="large-btn grey-btn"
              disabled={isLoading}
              onClick={onDeleteProposalFiles}>
              CANCEL
            </Button>
          </div>
        </Modal>
      )}

      {confirmModal && (
        <ConfirmModal
          title={
            (selectedProposalIds?.length || 0) > 1
              ? `Delete ${selectedProposalIds.length} proposals?`
              : 'Delete Proposal?'
          }
          body={
            trash
              ? 'This action cannot be undone'
              : (selectedProposalIds?.length || 0) > 1
              ? 'These proposals will be moved to trash.'
              : 'This proposal will be moved to trash.'
          }
          visible={confirmModal}
          cancelText="CANCEL"
          confirmText="DELETE"
          isSaving={isBulkDeleting}
          onCancel={() => {
            showConfirmModal(false);
            setSelectedProposalIds?.([]);
          }}
          onConfirm={confirmDeleteProposal}
        />
      )}

      {permissionErrorModal && (
        <ConfirmModal
          title={
            permissionErrorModalTitle ? 'Cannot Duplicate Proposal.' : 'Cannot Delete Proposal.'
          }
          body={permissionErrorModalBody || "You don't have permission to delete this proposal."}
          visible={permissionErrorModal}
          cancelText="Close"
          onCancel={() => showPermissionErrorModal(false)}
        />
      )}

      {duplicateModal && (
        <CreateDuplicateProposalModal
          modalType="Duplicate"
          user={user}
          visible={duplicateModal}
          onCancel={() => showDuplicateModal(false)}
          selectedProposal={
            selectedProposal ? selectedProposal : findProposalById(selectedProposalId)
          }
          onConfirm={duplicateProposal}
          isLoading={isDuplicating}
        />
      )}

      {restoreModal && (
        <RestoreProposalModal
          modalType="Restore"
          title={
            (selectedProposalIds?.length || 0) > 1
              ? `Restore ${selectedProposalIds.length} proposals?`
              : 'Restore Proposal?'
          }
          body={
            (selectedProposalIds?.length || 0) > 1
              ? 'You are restoring deleted proposals'
              : 'You are restoring deleted proposal'
          }
          cancelText="CANCEL"
          confirmText="RESTORE"
          user={user}
          visible={restoreModal}
          onCancel={() => {
            showRestoreModal(false);
            setSelectedProposalIds?.([]);
          }}
          selectedProposal={findProposalById(selectedProposalId)}
          isRestoring={isRestoring}
          onConfirm={retrieveProposal}
        />
      )}

      {generateInvoiceModal && (
        <GenerateInvoiceModal
          invoiceType={generateInvoiceModal}
          proposalId={selectedProposalId}
          organisations={user?.xero?.organisations || []}
          onCancel={() => setGenerateInvoiceModal(false)}
        />
      )}

      {userInfoModal && (
        <OnboardingModal
          user={user}
          refetchUser={refetchUser}
          saveUserProfileInfo={saveUserProfileInfo}
          createProposal={createProposalSignup}
          addUser={addUser}
          onCancel={() => setUserInfoModal(false)}
        />
      )}

      {showAddUserModal && (
        <AddUser
          user={user}
          plan={plan}
          usersTeam={teamMembers}
          visible={showAddUserModal}
          onCancel={onAddUserCancel}
          isSaving={isSaving}
          setPlan={setPlan}
          setShowAddUserModal={setShowAddUserModal}
          trial={getTrialTime() >= 0 ? true : false}
          onConfirm={(_, { membersToBeAdded, membersToBeDeleted }) => {
            setIsSaving(true);
            manageTeamMembers({
              variables: {
                membersToBeAdded,
                membersToBeDeleted,
              },
            });
          }}
        />
      )}

      {!!showShareProposalModal && (
        <ShareWithModal
          visible={!!showShareProposalModal}
          teamMembers={teamMembers.filter((teamMember) => teamMember.roles[0] === 'editor')}
          proposal={showShareProposalModal}
          onCancel={() => setShowShareProposalModal('')}
        />
      )}

      {moveModal && (
        <MoveLibraryModal
          visible={moveModal}
          onCancel={() => showMoveModal(false)}
          template={
            findProposalById(selectedProposalId) ||
            proposals.find((item) => item._id === selectedProposalId)
          }
          refetchTemplates={() => {}}
          isDashboard={true}
          treeData={modalTreeData}
          fetchTreeView={fetchTreeView}
          loadingTreeView={loadingTreeView}
          handleTreeSearch={renderHandleTreeSearch}
          addNewChildFromSearch={renderAddNewChildFromSearch}
        />
      )}

      {disabledEditingModal && (
        <Popconfirm
          visible={true}
          title="Bigger Screen Recommended"
          body="We noticed you're using a small device. Please use a desktop for the best proposal creation and editing experience."
          closable={false}
          cancelText={'Close'}
          onCancel={() => setDisabledEditingModal(false)}
          footer={null}
        />
      )}
    </>
  );
};

/**
 * Dashboard modals default props
 * @returns
 */
DashboardModals.defaultProps = {
  trash: false,
  proposals: [],
  teamMembers: [],
  selectedProposalId: null,
  showAddProposalModal: false,
  confirmModal: false,
  showAddUserModal: false,
  duplicateModal: false,
  permissionErrorModal: false,
  restoreModal: false,
  generateInvoiceModal: null,
  userInfoModal: false,
  showShareProposalModal: null,
  moveModal: false,
  disabledEditingModal: false,
  selectedProposalIds: [],
  setSelectedProposalIds: () => {},
};

/**
 * Dashboard modals prop types
 * @returns
 */
DashboardModals.propTypes = {
  user: PropTypes.instanceOf(Object).isRequired,
  trash: PropTypes.bool,
  proposals: PropTypes.instanceOf(Array).isRequired,
  teamMembers: PropTypes.instanceOf(Array).isRequired,
  selectedProposalId: PropTypes.string,
  popConfirmDetails: PropTypes.instanceOf(Object).isRequired,
  setPopConfirmDetails: PropTypes.func.isRequired,
  showAddProposalModal: PropTypes.bool,
  setShowAddProposalModal: PropTypes.func.isRequired,
  confirmModal: PropTypes.bool,
  showConfirmModal: PropTypes.func.isRequired,
  showAddUserModal: PropTypes.bool,
  setShowAddUserModal: PropTypes.func.isRequired,
  duplicateModal: PropTypes.bool,
  showDuplicateModal: PropTypes.func.isRequired,
  setUserInfoModal: PropTypes.func.isRequired,
  permissionErrorModal: PropTypes.bool,
  showPermissionErrorModal: PropTypes.func.isRequired,
  restoreModal: PropTypes.bool,
  showRestoreModal: PropTypes.func.isRequired,
  generateInvoiceModal: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setGenerateInvoiceModal: PropTypes.func.isRequired,
  userInfoModal: PropTypes.bool,
  showShareProposalModal: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  setShowShareProposalModal: PropTypes.func.isRequired,
  moveModal: PropTypes.bool,
  showMoveModal: PropTypes.func.isRequired,
  setLoadingPipedriveDealsDetails: PropTypes.func.isRequired,
  fetchMoreProposal: PropTypes.func.isRequired,
  disabledEditingModal: PropTypes.bool,
  setDisabledEditingModal: PropTypes.func.isRequired,
  findProposalById: PropTypes.func.isRequired,
  selectedProposalIds: PropTypes.arrayOf(PropTypes.string),
  setSelectedProposalIds: PropTypes.func,
};

export default DashboardModals;
